import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, Compass, LogOut, Menu, Search, Settings as SettingsIcon } from 'lucide-react';
import wordmark from '../../assets/masser-logo.png';
import { Icon, Skeleton } from '../ui';
import { useAuth } from '../../context/AuthContext';
import { useLive } from '../../context/LiveContext';
import { useData } from '../../context/DataContext';
import { usePrefs } from '../../context/PrefsContext';
import CommandPalette from './CommandPalette';
import Tour from './Tour';
import { cx } from '../../utils/format';

const NAV = [
  { to: '/', label: 'Overview', icon: 'LayoutDashboard', end: true },
  { to: '/fields', label: 'Greenhouses', icon: 'Sprout' },
  { to: '/stations', label: 'Stations', icon: 'RadioTower' },
  { to: '/alerts', label: 'Alerts', icon: 'BellRing', badge: true },
  { section: 'Insight' },
  { to: '/live', label: 'Live console', icon: 'Activity' },
  { to: '/analytics', label: 'Analytics', icon: 'BarChart3' },
  { section: 'Platform' },
  { to: '/system', label: 'System & simulator', icon: 'Server' },
  { to: '/settings', label: 'Settings', icon: 'Settings' },
  { to: '/help', label: 'Help & glossary', icon: 'LifeBuoy' },
];

export const Wordmark = ({ height = 30 }) => <img src={wordmark} alt="Masser" style={{ height }} />;

export default function AppShell() {
  const { user, logout } = useAuth();
  const { status, rate } = useLive();
  const { data } = useData();
  const prefs = usePrefs();
  const nav = useNavigate();
  const loc = useLocation();
  const [menu, setMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [palette, setPalette] = useState(false);
  const [tour, setTour] = useState(false);
  const userRef = useRef();

  useEffect(() => { setMenu(false); }, [loc.pathname]);
  useEffect(() => { if (data && !prefs.tourSeen) { const t = setTimeout(() => setTour(true), 900); return () => clearTimeout(t); } return undefined; }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeTour = useCallback(() => { setTour(false); prefs.set({ tourSeen: true }); }, [prefs]);

  useEffect(() => {
    const onKey = (e) => {
      const typing = /input|textarea|select/i.test(e.target.tagName);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette((p) => !p); }
      else if (!typing && e.key === '/') { e.preventDefault(); setPalette(true); }
      else if (!typing && e.key === '?') nav('/help');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nav, prefs]);
  useEffect(() => {
    const h = (e) => { if (userRef.current && !userRef.current.contains(e.target)) setUserMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const alerts = data ? data.kpis.alertsCritical + data.kpis.alertsWarning : 0;
  const initials = (user?.name || user?.email || '?').split(/[\s@.]/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('');
  const live = status === 'live';

  return (
    <div className="app">
      <aside className={cx('sidebar', menu && 'open')} data-tour="nav">
        <Link to="/" className="brand" aria-label="Masser, home"><Wordmark /></Link>
        {NAV.map((n, i) => n.section ? <div key={i} className="nav__section">{n.section}</div> : (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => cx('nav__item', isActive && 'active')}>
            <Icon name={n.icon} size={18} />{n.label}
            {n.badge && alerts > 0 && <span className="count">{alerts}</span>}
          </NavLink>
        ))}
        <div className="sidebar__foot">
          <b>{data?.simulator ? 'Simulated data' : 'Live hardware'}</b>
          <div style={{ marginTop: 3 }}>{data?.simulator ? 'Readings come from the built-in simulator.' : 'Readings come from the LoRa gateways.'}</div>
        </div>
      </aside>
      {menu && <div className="scrim" onClick={() => setMenu(false)} />}

      <div className="main">
        <header className="topbar">
          <button className="btn icon ghost burger" onClick={() => setMenu(true)} aria-label="Open menu"><Menu size={20} /></button>
          <button className="search" onClick={() => setPalette(true)} data-tour="search" aria-label="Search">
            <Search size={16} /><span className="truncate">Search…</span><span className="kbd">Ctrl K</span>
          </button>
          <div className="spacer" />
          <div className={cx('live-pill', live ? 'on' : 'off')} data-tour="live" title={live ? 'Receiving data in real time' : 'Reconnecting…'}>
            <i className={cx('dot', live && 'pulse')} />{live ? `Live${rate ? ` · ${rate}/min` : ''}` : status === 'connecting' ? 'Connecting…' : 'Offline'}
          </div>
          <Link to="/alerts" className="btn icon ghost" aria-label="Alerts" style={{ position: 'relative' }}>
            <Bell size={19} />
            {alerts > 0 && <span style={{ position: 'absolute', top: 3, right: 3, background: 'var(--crit)', color: '#fff', borderRadius: 2, fontSize: 10, fontWeight: 700, minWidth: 16, height: 16, display: 'grid', placeItems: 'center', padding: '0 4px' }}>{alerts}</span>}
          </Link>
          <button className="btn icon ghost" onClick={() => setTour(true)} aria-label="Guided tour" title="Guided tour"><Compass size={19} /></button>
          <div ref={userRef} style={{ position: 'relative' }}>
            <button className="btn ghost" onClick={() => setUserMenu((v) => !v)} style={{ padding: '0 6px 0 4px', gap: 8 }}>
              <span style={{ width: 28, height: 28, borderRadius: 2, background: 'var(--brand)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 800 }}>{initials}</span>
              <ChevronDown size={15} />
            </button>
            {userMenu && (
              <div className="card" style={{ position: 'absolute', right: 0, top: 44, width: 240, padding: 8, zIndex: 50, boxShadow: 'var(--shadow-lg)' }}>
                <div style={{ padding: '8px 10px' }}><b>{user?.name || 'User'}</b><div className="faint small truncate">{user?.email}</div><span className="badge brand" style={{ marginTop: 6 }}>{user?.role}</span></div>
                <hr className="hr" />
                <button className="nav__item" style={{ color: 'var(--text)' }} onClick={() => { setUserMenu(false); nav('/settings'); }}><SettingsIcon size={17} />Settings</button>
                <button className="nav__item" style={{ color: 'var(--crit)' }} onClick={logout}><LogOut size={17} />Sign out</button>
              </div>
            )}
          </div>
        </header>
        <Suspense fallback={<div className="page"><Skeleton h={34} w={260} /><div style={{ height: 18 }} /><Skeleton h={160} /></div>}>
          <Outlet />
        </Suspense>
      </div>
      <CommandPalette open={palette} onClose={() => setPalette(false)} onTour={() => setTour(true)} />
      <Tour open={tour} onClose={closeTour} />
    </div>
  );
}
